import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { AppointmentTypesApi, AppointmentTypesTypes, AppointmentTypesConstants } from '@frontend/api-appointment-types';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { ServiceProvidersApi } from '@frontend/api-service-providers';
import { Trans, useTranslation } from '@frontend/i18n';
import { useQuery } from '@frontend/react-query-helpers';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import {
  FormRow,
  Modal,
  ModalControlModalProps,
  useForm,
  TextField,
  DropdownField,
  Text,
  ChecklistField,
  Alert,
  SwitchField,
  MoneyField,
  NumberField,
  useAlert,
  ContentLoader,
  FormFieldActionTypes,
} from '@frontend/design-system';
import { ResponsiveModalActions } from '../settings';
import { modal, modalBody, helperText } from './styles';

const { appointmentTypeLabelColors, defaultAppointmentTypeLabelColor } = AppointmentTypesConstants;

function alphaSort<T>(key: keyof T, direction = 1) {
  return (a: T, b: T) => (String(a[key]).toLowerCase() < String(b[key]).toLowerCase() ? -1 * direction : 1 * direction);
}

interface Props extends ModalControlModalProps {
  appointmentType?: AppointmentTypesTypes.AppointmentType;
  maxOpeningsPermitted?: number;
  showLocationSelector: boolean;
  externalAppointmentTypes?: AppointmentTypesTypes.ExternalAppointmentType[];
  hasCreateAppointmentCapability: boolean;
  shouldShowAppointmentTypeLabelColors?: boolean;
  locationId: string;
  globalBookingDepositAmount?: number;
  hasGlobalBookingDeposit?: boolean;
  isPaymentsEnabled?: boolean;
  refetch: () => void;
}
export function AppointmentTypeModal({
  appointmentType,
  maxOpeningsPermitted,
  externalAppointmentTypes,
  hasCreateAppointmentCapability,
  shouldShowAppointmentTypeLabelColors = false,
  locationId,
  refetch,
  showLocationSelector,
  hasGlobalBookingDeposit = false,
  globalBookingDepositAmount = 0,
  isPaymentsEnabled = false,
  ...modalControlProps
}: Props) {
  const { t } = useTranslation('schedule');
  const alert = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const { isSmallMode } = useIsSmallMode();

  const hasExternalAppointmentTypes = !!externalAppointmentTypes?.length;

  const { formProps, values, changedValues, validate, getFieldProps, reset, seedValues, isComplete } = useForm({
    fields: {
      name: { type: 'text', required: true, value: appointmentType?.name },
      externalType: {
        type: 'dropdown',
        value: appointmentType?.externalAppointmentTypeId,
        required: hasExternalAppointmentTypes,
      },
      durationMinutes: {
        type: 'number',
        required: true,
        value: String(appointmentType?.durationMinutes),
        validator: ({ value }) => {
          if (value && Number(value) < 10) return t('Minimum of 10 minutes');
          if (value && Number(value) > 1440) return t('Maximum of 1440 minutes (24 hours)');
          return '';
        },
        validateOnChange: true,
        allowDecimal: false,
        allowNegative: false,
      },
      providerAssets: {
        type: 'checklist',
        value: appointmentType?.providerAssets?.filter(({ available }) => available).map((value) => value.id),
      },
      appointmentTypeLabelColor: {
        type: 'dropdown',
        value: appointmentType?.appointmentTypeLabelColor || defaultAppointmentTypeLabelColor,
      },
      appointmentTypePaymentFlag: {
        type: 'switch',
        value:
          !hasGlobalBookingDeposit &&
          !!appointmentType?.hasBookingDeposit &&
          !!appointmentType?.bookingDepositAmount &&
          Number(appointmentType.bookingDepositAmount) > 0,
      },
      appointmentTypePaymentAmount: {
        type: 'money',
        value: (appointmentType?.bookingDepositAmount || 0).toFixed(2),
        validator: ({ value }, formValues) => {
          const amount = Number(value);
          const isToggleOn = formValues?.appointmentTypePaymentFlag?.value === true;
          if (!isToggleOn) return '';
          if (amount === 0) return t('Amount must be greater than 0');
          if (amount % 1 !== 0) return t('Whole dollar amounts only');
          return '';
        },
      },
    },
    fieldStateReducer: (state, action) => {
      if (
        action.type === FormFieldActionTypes.Update &&
        action.payload.name === 'appointmentTypePaymentFlag' &&
        action.payload.value === false
      ) {
        return {
          ...state,
          appointmentTypePaymentAmount: {
            ...state.appointmentTypePaymentAmount,
            value: (appointmentType?.bookingDepositAmount || 0).toFixed(2), // reset value to initial value when switch is turned off
            error: '', // reset error message when switch is turned off
          },
        };
      }
      return state;
    },
    onSubmit: async (values) => {
      if (values.appointmentTypePaymentFlag === true && Number(values.appointmentTypePaymentAmount) < 1) {
        validate();
        return;
      }

      const providers = values.providerAssets as string[];

      const appointmentTypePaymentConfigPayload = {
        hasBookingDeposit: values.appointmentTypePaymentFlag,
        bookingDepositAmount: Number(values.appointmentTypePaymentAmount || 0),
      };

      const payload = {
        ...appointmentType,
        durationMinutes: Math.round(+(values?.durationMinutes ?? 0) / 5) * 5,
        name: values.name as string,
        locationId,
        providerAssets: updateProviders({
          assets: providerAppointmentOptions,
          list: providers as string[],
        }) as AppointmentTypesTypes.ProviderAsset[],
        appointmentTypeLocationIds: appointmentType?.appointmentTypeLocationIds ?? [],
        externalAppointmentTypeId: values.externalType as string,
        //TODO -  Adding the maxOpeningsPermitted value for the 'Required Number of Appt. Times' ApptRequestConfig. This is a temporary solution until we can refactor the ApptRequestConfig for clarity. We only need to set a maximum number of openings for the 'Required Number of Appt. Times' ApptRequestConfig in Appointment Settings, but this value is being set to 'minOpeningsPermitted'.
        minOpeningsPermitted: maxOpeningsPermitted,
        ...(isPaymentsEnabled && appointmentTypePaymentConfigPayload),
        ...(shouldShowAppointmentTypeLabelColors && { appointmentTypeLabelColor: values.appointmentTypeLabelColor }),
      };

      try {
        setIsLoading(true);
        await AppointmentTypesApi.upsertAppointmentType(payload, locationId);
        if (payload.id) {
          alert.success(t('Successfully updated appointment type'));
        } else {
          alert.success(t('Successfully created appointment type'));
        }
      } catch (error: any) {
        console.error(error);
        if (error && payload.id) {
          alert.error(t('Failed to update appointment type'));
        } else {
          alert.error(t('Failed to create appointment type'));
        }
      } finally {
        setIsLoading(false);
        modalControlProps.onClose();
        refetch();
        reset();
      }
    },
    allowInvalidSubmission: false,
    computeChangedValues: true,
  });

  type Assets = Array<AppointmentTypesTypes.ProviderAsset>;
  type UpdateAssetsProps = {
    assets: Assets;
    list: string[];
  };

  const updateProviders = ({ assets, list }: UpdateAssetsProps): Assets => {
    const providerMap = assets?.map((asset) => {
      return {
        ...asset,
        available: list.includes(asset.id),
      };
    });
    return providerMap;
  };

  const seedAppointmentType = (appointmentType?: AppointmentTypesTypes.AppointmentType) => {
    if (appointmentType) {
      seedValues({
        name: appointmentType.name,
        externalType: appointmentType.externalAppointmentTypeId,
        providerAssets: appointmentType.providerAssets?.filter(({ available }) => available).map((value) => value.id),
        durationMinutes: String(appointmentType.durationMinutes),
      });
    }
    reset();
  };

  useEffect(() => {
    seedAppointmentType(appointmentType);
  }, [appointmentType?.id, modalControlProps.show]);

  const { data: providersData } = useQuery({
    queryKey: ['schedule', 'appointment-type-modal', 'providers', locationId],
    queryFn: () => ServiceProvidersApi.listServiceProviders(locationId),
    onSettled: () => SchedulerV3.trackScheduleAPIVersionChange('v2', { endpoint: 'listServiceProviders' }),
  });

  const providerAppointmentOptions = useMemo(() => {
    const providerArray = appointmentType?.providerAssets ? appointmentType.providerAssets : providersData;

    return (
      (providerArray ?? []).map((prov: any) => ({
        id: prov.id,
        available: true,
        providerName: appointmentType?.providerAssets ? prov.providerName : `${prov.firstName} ${prov.lastName}`,
      })) || []
    );
  }, [providersData, appointmentType?.providerAssets]);

  const providersChecklist = () => {
    const providerArray = appointmentType?.providerAssets ? appointmentType.providerAssets : providerAppointmentOptions;

    return providerArray?.sort(alphaSort<AppointmentTypesTypes.ProviderAsset>('providerName')).map((provider) => (
      <ChecklistField.Option key={provider.id} name={provider.id} trackingId={provider.id}>
        {provider.providerName}
      </ChecklistField.Option>
    ));
  };

  const handleSaveAppointmentType = (event?: React.FormEvent<Element>) => {
    if (!event) {
      return;
    }
    formProps.onSubmit(event);
  };

  const handleCancel = () => {
    modalControlProps.onClose();
    reset();
    seedAppointmentType(appointmentType);
  };

  const shouldDisableSaveBtn = !changedValues || !isComplete;

  return (
    <Modal.Responsive {...modalControlProps} minWidth={600} maxWidth={600} css={modal(isSmallMode)}>
      <ContentLoader show={isLoading} />
      <Modal.Header>
        {appointmentType?.id ? t('Edit Appointment Type Settings') : t('Add Appointment Type Settings')}
      </Modal.Header>
      <Modal.Body css={modalBody}>
        <form>
          {shouldShowAppointmentTypeLabelColors ? (
            <FormRow cols={[80, 20]}>
              <TextField {...getFieldProps('name')} label={`*${t('Display Name')}`} />
              <DropdownField {...getFieldProps('appointmentTypeLabelColor')} label={`${t('Color')}`}>
                {appointmentTypeLabelColors.map((color) => {
                  return (
                    <DropdownField.Option key={color} value={`${color}`}>
                      <div
                        aria-label={`${color}`}
                        css={css`
                          background-color: ${color};
                          height: 24px;
                          width: 24px;
                          border-radius: ${theme.borderRadius.full};
                        `}
                      ></div>
                    </DropdownField.Option>
                  );
                })}
              </DropdownField>
            </FormRow>
          ) : (
            <FormRow>
              <TextField {...getFieldProps('name')} label={`*${t('Display Name')}`} />
            </FormRow>
          )}
          {hasExternalAppointmentTypes && (
            <FormRow>
              <DropdownField {...getFieldProps('externalType')} label={`*${t('External Appointment Type')}`}>
                <DropdownField.Option value=''>{t('None')}</DropdownField.Option>
                {externalAppointmentTypes.map((externalAppointmentType, index) => {
                  return (
                    <DropdownField.Option
                      key={index}
                      value={externalAppointmentType.id || ''}
                      searchValue={externalAppointmentType.name}
                    >
                      <Text as='span' color='light'>
                        {externalAppointmentType.name}
                      </Text>
                    </DropdownField.Option>
                  );
                })}
              </DropdownField>
            </FormRow>
          )}
          {!hasExternalAppointmentTypes && hasCreateAppointmentCapability && (
            <FormRow>
              <Alert type='warning'>
                {t(
                  'We were unable to sync appointment types from your PMS. Please ensure there are appointment types in your PMS. If there are please call Weave Support.'
                )}
              </Alert>
            </FormRow>
          )}

          <FormRow>
            <NumberField
              {...getFieldProps('durationMinutes')}
              label={`*${t('Duration (minutes)')}`}
              min={10}
              max={1440}
            />
          </FormRow>

          {isPaymentsEnabled && (
            <>
              <FormRow>
                <div>
                  <SwitchField
                    {...getFieldProps('appointmentTypePaymentFlag')}
                    label={t('Request below amount for the appointment type')}
                    labelPlacement='right'
                    disabled={hasGlobalBookingDeposit}
                  />
                  {hasGlobalBookingDeposit && (
                    <Text color='light' size='small' css={{ marginTop: theme.spacing(1) }}>
                      <Trans t={t} values={{ globalBookingDepositAmount }}>
                        <strong>Note:</strong> The system is currently set to request a payment deposit of
                        <strong> ${{ globalBookingDepositAmount }}</strong>, as specified in the Appointment Type
                        Payments Settings. If you wish to customize the payment deposit for this specific appointment
                        type, kindly ensure that you disable the global Appointment Type Payments Settings on the
                        appointment type page.
                      </Trans>
                    </Text>
                  )}
                </div>
              </FormRow>
              <FormRow>
                <MoneyField
                  {...getFieldProps('appointmentTypePaymentAmount')}
                  css={{ width: 200 }}
                  label={t('Amount')}
                  helperText={t('Whole dollar amounts only')}
                  disabled={!values.appointmentTypePaymentFlag || hasGlobalBookingDeposit}
                />
              </FormRow>
            </>
          )}

          <Text size='small' color='light' css={helperText}>
            {`*${t('Required fields')}`}
          </Text>

          <FormRow>
            <ChecklistField
              {...getFieldProps('providerAssets')}
              label={t('Select available providers:')}
              labelPlacement='left'
            >
              {providersChecklist()}
            </ChecklistField>
          </FormRow>
        </form>
      </Modal.Body>
      <ResponsiveModalActions
        primaryLabel={t('Save')}
        disablePrimary={shouldDisableSaveBtn}
        onPrimaryClick={handleSaveAppointmentType}
        secondaryLabel={t('Cancel')}
        onSecondaryClick={handleCancel}
      />
    </Modal.Responsive>
  );
}
